// Norethisterone 5 mg for period delay. Female patients only, aged 16
// and over. 5 mg is NOT a contraceptive dose — barrier contraception is
// required throughout. Maximum 17 days treatment (up to 14 days delay).
// Exclusion list includes VTE, arterial thromboembolism, liver
// conditions, and hormone-sensitive malignancy.

import { registerTier2Config } from "../tier2ConfigRegistry";
import { PGD_IDS } from "../../pgds/pgdIds";

registerTier2Config({
  pgdId: PGD_IDS.NORETHISTERONE,
  displayName: "Norethisterone 5 mg (period delay)",
  serviceType: "supply",
  minimumAgeYears: 16,
  indicationSummary:
    "Short-term delay of menstruation in female patients aged 16 and over for a specific event or travel. Norethisterone 5 mg is not a contraceptive \u2014 barrier contraception must be used throughout treatment.",

  brands: [
    {
      brandId: "norethisterone-5mg",
      brandName: "Norethisterone 5 mg tablets",
      minimumAgeYears: 16,
      route: "supply",
      doseDescription: "One 5 mg tablet three times daily, starting 3 days before the expected onset of menstruation",
      administrationNotes:
        "Supply norethisterone 5 mg tablets. Label: one tablet three times daily, starting 3 days before the expected onset of menstruation. Continue for the duration of delay required (maximum 14 days of delay; maximum 17 days total treatment). Menstruation usually follows within 3 days of stopping. This is not a contraceptive dose \u2014 advise use of barrier contraception throughout.",
    },
  ],

  schedules: [
    {
      scheduleId: "norethisterone-tds",
      label: "Three times daily \u2014 start 3 days before expected onset of menstruation; maximum 17 days treatment",
      offLabel: false,
      doses: [
        {
          doseNumber: 1,
          label: "Start: 3 days before the expected onset of menstruation",
        },
      ],
      completionNotes:
        "Continue three times daily for the duration of delay required. Maximum 14 days of delay; maximum 17 days total treatment. Menstruation usually follows within 3 days of stopping. This product is not a contraceptive \u2014 advise barrier contraception throughout. Do not supply more than 14 days of delay in any 6-month period. More frequent use requires referral to the patient's GP.",
    },
  ],

  capturesPriorDoseHistory: false,

  supplyGates: [
    "Patient confirmed female.",
    "Patient age confirmed as 16 years or over.",
    "Confirmed that barrier contraception will be used throughout treatment (norethisterone 5 mg is not a contraceptive dose).",
  ],

  exclusions: [
    {
      checkId: "noreth-ex-age-sex",
      question: "Is the patient male, or is the patient under 16 years of age?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Norethisterone for period delay under this PGD is only for female patients aged 16 and over. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "noreth-ex-pregnancy-breastfeeding",
      question: "Is the patient known or suspected to be pregnant, or breastfeeding?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Norethisterone is contraindicated in pregnancy and breastfeeding. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "noreth-ex-no-barrier-contraception",
      question: "Is the patient unwilling to use barrier contraception throughout the treatment period?",
      disqualifyingAnswer: "yes",
      disqualificationMessage:
        "Norethisterone 5 mg is not a contraceptive dose and does not protect against pregnancy. If the patient is unwilling to use barrier contraception, do not supply.",
      referralMessage: "Refer to the patient's GP for alternative management.",
    },
    {
      checkId: "noreth-ex-hypersensitivity",
      question: "Does the patient have known hypersensitivity to norethisterone or any excipient of the product (including lactose and maize starch)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Norethisterone is contraindicated in this patient. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "noreth-ex-thromboembolism",
      question: "Does the patient have any of the following: a current or past history of idiopathic venous thromboembolism (DVT or pulmonary embolism), active or recent arterial thromboembolic disease (e.g. angina, myocardial infarction, stroke), or a high risk of thrombosis including a history or prodromal signs of thrombosis (TIA, severe angina)?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "A history or current presence of venous or arterial thromboembolic disease, or high thrombosis risk, is a contraindication to norethisterone. Do not supply.",
      referralMessage: "Refer to the patient's GP for advice on menstrual management.",
    },
    {
      checkId: "noreth-ex-liver-malignancy",
      question: "Does the patient have any of the following: disturbed liver function, jaundice, a history or current diagnosis of a liver tumour (benign or malignant), or a known, past, or suspected sex-hormone-dependent malignancy including breast cancer?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Disturbed liver function, liver tumours, and sex-hormone-dependent malignancy are each a contraindication to norethisterone. Do not supply.",
      referralMessage: "Refer to the patient's GP or oncologist.",
    },
    {
      checkId: "noreth-ex-pregnancy-history-conditions",
      question: "Does the patient have a history during a previous pregnancy of any of the following: idiopathic jaundice, severe pruritus, or pemphigoid gestationis?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "A history of these pregnancy-related conditions is a contraindication to norethisterone. Do not supply.",
      referralMessage: "Refer to the patient's GP.",
    },
    {
      checkId: "noreth-ex-bleeding-porphyria",
      question: "Does the patient have undiagnosed abnormal genital bleeding or a diagnosis of porphyria?",
      disqualifyingAnswer: "yes",
      disqualificationMessage: "Undiagnosed abnormal genital bleeding and porphyria are each a contraindication to norethisterone. Do not supply.",
      referralMessage: "Refer to the patient's GP. Undiagnosed genital bleeding requires investigation before any hormonal treatment is considered.",
    },
  ],

  cautions: [
    {
      checkId: "noreth-caut-warfarin",
      question: "Is the patient taking warfarin or another anticoagulant?",
      cautionMessage:
        "Norethisterone is not recommended in patients taking warfarin or other anticoagulants due to the risk of altered anticoagulant effect. Carefully weigh the risks and benefits before proceeding. If proceeding, advise increased INR monitoring and notify the patient's GP.",
    },
    {
      checkId: "noreth-caut-enzyme-inducers",
      question:
        "Is the patient taking enzyme-inducing medicines including anticonvulsants (e.g. phenytoin, carbamazepine, phenobarbital), rifampicin, rifabutin, nevirapine, efavirenz, ritonavir, nelfinavir, or tetracyclines?",
      cautionMessage:
        "Enzyme-inducing medicines may increase the metabolism of norethisterone and reduce its efficacy, potentially reducing the reliability of period delay and increasing the risk of breakthrough bleeding. Advise the patient of reduced reliability. Consider whether an alternative approach is more appropriate.",
    },
    {
      checkId: "noreth-caut-st-johns-wort",
      question: "Is the patient taking St John's Wort (Hypericum perforatum)?",
      cautionMessage: "St John's Wort may reduce the plasma concentration of norethisterone and reduce its efficacy. Advise the patient of reduced reliability. Advise against concurrent use where possible.",
    },
    {
      checkId: "noreth-caut-doxycycline",
      question: "Is the patient also being supplied with doxycycline for malaria prophylaxis?",
      cautionMessage:
        "Doxycycline may reduce the efficacy of norethisterone and increase the risk of breakthrough bleeding. Advise the patient to use barrier contraception throughout the doxycycline course and for 7 days after stopping. This interaction is also flagged in the co-administration matrix if both are in the basket.",
    },
    {
      checkId: "noreth-caut-stop-warning",
      question: "Has the pharmacist counselled the patient on warning symptoms that require stopping norethisterone immediately?",
      cautionMessage:
        "Advise the patient to stop norethisterone immediately and seek urgent medical attention if any of the following develop: jaundice or deteriorating liver function; a significant rise in blood pressure; a new or worsening migraine or an unusually severe headache; sudden visual or hearing disturbance; first signs of thrombophlebitis or thromboembolism (pain or swelling in the leg, sudden chest pain or breathlessness); or pregnancy. Document that this advice was given.",
    },
    {
      checkId: "noreth-caut-surgery",
      question: "Is major surgery or prolonged immobilisation planned within the next 6 weeks?",
      cautionMessage:
        "Norethisterone should be stopped at least 6 weeks before planned major surgery or prolonged immobilisation due to the increased risk of thromboembolism. Advise the patient to inform their surgeon and GP.",
    },
  ],

  requiresICVP: false,
  requiresLariamCard: false,
  requiresYellowCardDiscussion: false,
  requiresCHISNotificationUnder19: false,
  requiresGPNotification: true,
  standardObservationMinutes: 0,
  extendedObservationMinutes: 0,

  patientAdvicePoints: [
    "Take one tablet three times a day, starting 3 days before your period is expected to begin.",
    "Continue taking the tablets for as long as you need to delay your period, up to a maximum of 17 days total (14 days of delay).",
    "Your period should return within 3 days of stopping the tablets.",
    "This is not a contraceptive. You must use barrier contraception (e.g. condoms) throughout the entire time you are taking norethisterone.",
    "If you are also taking doxycycline for malaria prevention: doxycycline may reduce the reliability of norethisterone for period delay and increase the chance of breakthrough bleeding. Use barrier contraception throughout and for 7 days after stopping doxycycline.",
    "Stop taking norethisterone immediately and seek urgent medical attention if you develop: jaundice (yellowing of the skin or eyes); a severe or unusual headache or migraine; sudden changes in vision or hearing; pain, swelling, or redness in the leg; sudden chest pain or difficulty breathing; or signs of pregnancy.",
    "If you are due to have surgery or will be immobile for a long period within the next 6 weeks, tell your GP and surgeon that you are taking norethisterone.",
    "Do not take norethisterone for more than 14 days of delay in any 6-month period without discussing with your GP.",
  ],
});
